import type { IconDefinition } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'

// Modelos do Zgrid2 (z/zgrid2.svelte). Independente de zgrid.ts de propósito — nada aqui é importado de lá.
//
// RETROCOMPATÍVEL com a configuração do Zgrid: um GridClass montado com zgrid.ts pode ser passado direto ao
// <Zgrid2>. Isso depende de manter aqui os MESMOS nomes de classes, de enums e de membros de enum (com os
// mesmos valores) — o TypeScript só aceita um enum de outro arquivo quando nome e membros batem. Não renomeie
// nem reordene; membro novo de enum vai sempre no fim. Propriedades que só existem aqui estão marcadas "novo".

export class GridClass {
    public constructor(init?: Partial<GridClass>) {
        Object.assign(this, init)
        this._updatedAt = new Date()
    }
    // novo: chave da escolha feita no botão "Colunas", salva no navegador (localStorage). sem id, o zgrid2 usa uma
    // chave automática pelo título do grid — informe id quando duas telas tiverem grids de mesmo título que não
    // devem compartilhar a escolha, ou quando o título variar (ex.: contém nome ou data).
    id?: string = null
    defaultEmptyValue: string = '—'
    title?: string = ''
    subtitle?: string = ''
    cols: GridCol[] = []
    lines: GridLine[] = []
    showHeaders: boolean = true
    showLabels: boolean = true
    // busca + menu "Ações desta tabela" (imprimir, compartilhar, exportar)
    showActs: boolean = true
    // ordenar/filtrar pelo cabeçalho das colunas
    showFilter: boolean = true
    showLines: boolean = true
    // novo: false esconde o botão "Colunas"
    showColumns?: boolean = true
    // act com "active" definido (true/false) vira item do grupo segmentado da barra de ferramentas; sem "active",
    // vira botão comum no cabeçalho. ver GridButton.active
    acts: GridButton[] = []
    _updatedAt: Date = new Date()
    fontSize?: string = null
    minWidth?: string = ''
    box: GridBox[] = []
    // novo: rótulo da linha de totais (col.showTotal)
    totalLabel?: string = 'Total'
    // novo
    searchPlaceholder?: string = 'Buscar nos dados carregados'
}

export enum GridColTypeEnum {
    string,
    int,
    float,
    float_2, // 2 casas decimais
    date,
    button,
    badge,
    datetime,
    datetime_friendly,
    time,
    time_short,
    phone,
    email,
    toggle,
    zinput,
    percent,
    checkbox, // seleção de linha pra ações em massa — ver GridCol.bulkActions e GridLine.checked
    currency, // novo: valor monetário na moeda da empresa (utils.toReal)
}

export enum GridBadgeClassEnum {
    badge_alert = 'zbadge_alert',
    badge_danger = 'zbadge_danger',
    badge_success = 'zbadge_success',
    badge_default = 'zbadge_default',
    badge_blue = 'zbadge_blue',
    badge_purple = 'zbadge_purple',
    badge_disabled = 'zbadge_disabled',
}

export enum GridColAlignEnum {
    unset,
    left,
    center,
    right,
}

export enum GridItemShow {
    show,
    hide,
}

// novo: estado de uma coluna no botão "Colunas" (ver GridCol.visibility)
export enum GridColVisibilityEnum {
    visible,
    hidden,
    fixed,
}

export class GridCol {
    public constructor(init?: Partial<GridCol>) {
        Object.assign(this, init)
    }
    size?: number = 0
    type?: GridColTypeEnum = GridColTypeEnum.string
    // unset alinha número e botão à direita, checkbox ao centro e o resto à esquerda
    align?: GridColAlignEnum = GridColAlignEnum.unset
    class?: string = ''
    content: string = ''
    // false tira a coluna de vez (nem aparece no botão "Colunas")
    show?: boolean = true
    showTotal?: boolean = false
    mobile: GridItemShow = GridItemShow.show
    maxLength: number = null
    // filtro inicial (valores exibidos). depois disso o filtro vive no estado interno do zgrid2
    filter?: string[] = []
    showTotalBox?: boolean = false
    isGroup?: boolean = false
    // só pra type=checkbox: botões da barra verde que surge ao marcar linhas. cada callback recebe um array com
    // o "content" (ver GridSlot) desta coluna em cada linha marcada e visível.
    bulkActions?: GridButton[] = []
    // novo: valor da célula em negrito
    bold?: boolean = false
    // novo: exibição no botão "Colunas". visible (padrão) e hidden começam exibida/oculta e o usuário pode trocar;
    // fixed fica sempre exibida (aparece na lista só pra reordenar)
    visibility?: GridColVisibilityEnum = GridColVisibilityEnum.visible
    // novo: null = automático (não ordena botão, checkbox, toggle e zinput)
    sortable?: boolean = null
    // novo: null = automático (também não filtra colunas numéricas)
    filterable?: boolean = null
}

export class GridBox {
    label: string
    value: number
    decimals?: number
    class?: string = ''
}

export class GridLine {
    public constructor(init?: Partial<GridLine>) {
        Object.assign(this, init)
    }
    // false tira a linha do CSV exportado (mesmo sentido do Zgrid) — não confundir com "checked"
    selected?: boolean = true
    slots: GridSlot[] = []
    // false esconde a linha. a busca e os filtros do zgrid2 não mexem aqui
    visible?: boolean = true
    removed?: boolean = false
    class: string = ''
    // marcação da linha via coluna type=checkbox
    checked?: boolean = false
}

export class GridSlot {
    public constructor(init?: Partial<GridSlot>) {
        Object.assign(this, init)
    }
    content: any = null
    // novo: texto menor exibido abaixo do valor principal. com subtitle, a badge do slot vai para essa linha
    subtitle?: string = ''
    badge?: GridBadge = null
    action?: GridButton = null
    class?: string = ''
}

export class GridButton {
    public constructor(init?: Partial<GridButton>) {
        Object.assign(this, init)
    }
    label: string = ''
    title?: string = ''
    icon?: IconDefinition = null
    callback: Function | any = null
    class?: string = ''
    mobile?: GridItemShow = GridItemShow.show
    mobileShowLabel?: boolean = true
    disabled?: boolean = false
    color?: 'green' | 'red' | 'yellow' | 'secondary' | 'primary' = 'secondary'
    // novo, só para grid.acts: true/false coloca o botão no grupo segmentado (true = selecionado). o zgrid2 marca
    // o clicado na hora; ao recriar o grid, a tela deve informar o active conforme o próprio estado.
    active?: boolean = null
    // novo, só para grid.acts: abre um popup em vez de chamar o callback no clique.
    // - "date": seletor de período (date-select). o callback recebe { from, to } e o botão mostra o intervalo
    // - "select": lista de options. o callback recebe o value escolhido e o botão mostra o label da opção atual
    // no grupo segmentado (com "active"), o botão só fica ativo depois da escolha, e só então mostra o valor
    type?: 'button' | 'date' | 'select' = 'button'
    // novo, só para type "date": período atual — o popup abre nele e o botão o exibe
    dateFrom?: Date = null
    dateTo?: Date = null
    // novo, só para type "select": opções do popup e a escolhida no momento
    options?: { value: any; label: string }[] = []
    value?: any = null
}

export class GridToggle {
    public constructor(init?: Partial<GridToggle>) {
        Object.assign(this, init)
    }
    label: string = ''
    checked: boolean = false
    callback: Function = null
    class?: string = ''
    mobile?: GridItemShow = GridItemShow.show
    disabled?: boolean = false
}

export class GridZInput {
    public constructor(init?: Partial<GridZInput>) {
        Object.assign(this, init)
    }
    type: 'text' | 'number' | 'date' | 'select' | 'toggle' | 'multiselect' = 'text'
    label: string = ''
    options: { value: any; label: string }[] = []
    value: boolean = false
    callback: Function = null
    class?: string = ''
    mobile?: GridItemShow = GridItemShow.show
    disabled?: boolean = false
    help?: string = null
    // texto exibido com o campo vazio — ex.: o valor herdado quando o campo vazio significa "usa o padrão"
    placeholder?: string = ''
}

export class GridBadge {
    public constructor(init?: Partial<GridBadge>) {
        Object.assign(this, init)
    }
    label: string = ''
    color?: string = ''
    class?: GridBadgeClassEnum | string = GridBadgeClassEnum.badge_default
}

// flag "grid" com valor 2 liga o Zgrid2. o valor vem do cache de flags que o init já carregou (#loadFeatureFlags),
// por isso a leitura é síncrona. o próprio Zgrid consulta isto e se troca pelo Zgrid2, então a tela não precisa fazer
// nada; as telas só usam esta função quando querem ajustar a configuração do grid (ordem de botões, acts).
export function isGrid2Enabled(): boolean {
    if (typeof window === 'undefined') return false
    return !!window.featureFlags?.includes('grid') && String(window._flags?.flags?.grid) === '2'
}

// ###################################################################### helpers de conteúdo
// usados pela célula, busca, filtro, ordenação e CSV — um único lugar decide como cada tipo vira texto.

const NUMERIC_TYPES = [GridColTypeEnum.int, GridColTypeEnum.float, GridColTypeEnum.float_2, GridColTypeEnum.percent, GridColTypeEnum.currency]
const INTERACTIVE_TYPES = [GridColTypeEnum.button, GridColTypeEnum.checkbox, GridColTypeEnum.toggle, GridColTypeEnum.zinput]

export function isNumericType(type: GridColTypeEnum): boolean {
    return NUMERIC_TYPES.includes(type)
}

export function isInteractiveType(type: GridColTypeEnum): boolean {
    return INTERACTIVE_TYPES.includes(type)
}

export function isEmptyContent(value: any): boolean {
    return value === null || value === undefined || value === ''
}

export function toNumber(value: any): number {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (isEmptyContent(value) || typeof value === 'object') return null
    const parsed = Number(String(value).replace('%', '').trim())
    return Number.isFinite(parsed) ? parsed : null
}

export function normalizeText(value: any): string {
    return String(value ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim()
}

// rótulos de conteúdo em lista ou objeto (badges, botões): [{ label }] -> ['label']
export function labelsOf(content: any): string[] {
    const items = Array.isArray(content) ? content : [content]
    return items
        .filter((item) => !isEmptyContent(item))
        .map((item) => {
            if (item instanceof Date) return dayjs(item).format('DD/MM/YYYY HH:mm')
            if (typeof item === 'object') return String(item.label ?? item.content ?? '')
            return String(item)
        })
        .filter(Boolean)
}

function formatDate(content: any, format: string): string {
    const date = dayjs(content)
    return date.isValid() ? date.format(format) : String(content)
}

export function formatContent(type: GridColTypeEnum, content: any, emptyValue: string = '—', isMobile: boolean = false): string {
    if (isEmptyContent(content)) return emptyValue
    const number = toNumber(content)
    switch (type) {
        case GridColTypeEnum.date:
            return formatDate(content, isMobile ? 'DD/MM/YY' : 'DD/MM/YYYY')
        case GridColTypeEnum.datetime:
            return formatDate(content, isMobile ? 'DD/MM/YY HH:mm' : 'DD/MM/YYYY HH:mm:ss')
        case GridColTypeEnum.datetime_friendly:
            return window.api.utils.utils.dateFriendly(content)
        case GridColTypeEnum.time:
            return formatDate(content, 'HH:mm:ss')
        case GridColTypeEnum.time_short:
            return formatDate(content, 'HH:mm')
        case GridColTypeEnum.int:
            // sem separador de milhar, igual ao Zgrid: coluna int costuma guardar ano, código ou número de documento
            return String(content)
        case GridColTypeEnum.float:
            return number === null ? String(content) : number.toLocaleString(undefined, { maximumFractionDigits: 4 })
        case GridColTypeEnum.float_2:
            return number === null ? String(content) : number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        case GridColTypeEnum.currency:
            return number === null ? String(content) : window.api.utils.utils.toReal(number)
        case GridColTypeEnum.percent:
            if (number === null) return String(content)
            return number > 1000 ? '1000%+' : Math.round(number) + '%'
        case GridColTypeEnum.phone:
            return window.api.utils.utils.phoneFriendly(content)
        case GridColTypeEnum.email:
            return String(content).toLowerCase().trim()
        default:
            if (content instanceof Date) return formatDate(content, 'DD/MM/YYYY HH:mm')
            if (typeof content === 'object') return labelsOf(content).join(', ') || emptyValue
            return String(content)
    }
}

// valor usado pra ordenar. null = vazio (o zgrid2 sempre coloca vazios no fim)
export function sortValueOf(col: GridCol, slot: GridSlot): number | string {
    const content = slot?.content
    switch (col.type) {
        case GridColTypeEnum.date:
        case GridColTypeEnum.datetime:
        case GridColTypeEnum.datetime_friendly: {
            if (isEmptyContent(content)) return null
            const date = dayjs(content)
            return date.isValid() ? date.valueOf() : null
        }
        case GridColTypeEnum.time:
        case GridColTypeEnum.time_short: {
            if (isEmptyContent(content)) return null
            const date = dayjs(content)
            return date.isValid() ? date.hour() * 3600 + date.minute() * 60 + date.second() : null
        }
        case GridColTypeEnum.toggle:
            return content ? (content.checked ? 1 : 0) : null
        default:
            if (isNumericType(col.type)) return toNumber(content)
            if (isEmptyContent(content)) return null
            if (typeof content === 'object' && !(content instanceof Date)) return labelsOf(content).join(', ') || null
            return formatContent(col.type, content, '')
    }
}

export function compareSortValues(a: number | string, b: number | string): number {
    if (a === null && b === null) return 0
    if (a === null) return 1
    if (b === null) return -1
    if (typeof a === 'number' && typeof b === 'number') return a - b
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

// chaves de filtro de uma célula. é o próprio texto exibido (datas agrupam pelo dia/hora mostrados), e '' = sem valor
export function filterKeysOf(col: GridCol, slot: GridSlot): string[] {
    const content = slot?.content
    if (col.type === GridColTypeEnum.badge || Array.isArray(content)) {
        const labels = labelsOf(content)
        return labels.length ? labels : ['']
    }
    if (isEmptyContent(content)) return ['']
    return [formatContent(col.type, content, '')]
}

export function searchTextOf(col: GridCol, slot: GridSlot): string {
    if (!slot || isInteractiveType(col.type)) return ''
    const content = slot.content
    const parts: string[] = []
    if (col.type === GridColTypeEnum.badge || Array.isArray(content)) {
        parts.push(...labelsOf(content))
    } else if (!isEmptyContent(content)) {
        parts.push(formatContent(col.type, content, ''))
        if (typeof content !== 'object') parts.push(String(content))
    }
    if (slot.subtitle) parts.push(slot.subtitle)
    if (slot.badge?.label) parts.push(slot.badge.label)
    return parts.join(' ')
}

export function csvValueOf(col: GridCol, slot: GridSlot): string {
    const content = slot?.content
    if (col.type === GridColTypeEnum.badge || Array.isArray(content)) return labelsOf(content).join(', ')
    if (isEmptyContent(content)) return ''
    const number = toNumber(content)
    switch (col.type) {
        case GridColTypeEnum.date:
            return formatDate(content, 'DD/MM/YYYY')
        case GridColTypeEnum.datetime:
        case GridColTypeEnum.datetime_friendly:
            return formatDate(content, 'DD/MM/YYYY HH:mm:ss')
        case GridColTypeEnum.time:
            return formatDate(content, 'HH:mm:ss')
        case GridColTypeEnum.time_short:
            return formatDate(content, 'HH:mm')
        case GridColTypeEnum.float_2:
        case GridColTypeEnum.currency:
            return number === null ? String(content) : number.toFixed(2).replace('.', ',')
        case GridColTypeEnum.float:
            return String(number ?? content).replace('.', ',')
        case GridColTypeEnum.percent:
            return number === null ? String(content) : Math.round(number) + '%'
        case GridColTypeEnum.phone:
            return window.api.utils.utils.phoneFriendly(content)
        default:
            if (content instanceof Date) return formatDate(content, 'DD/MM/YYYY HH:mm:ss')
            if (typeof content === 'object') return labelsOf(content).join(', ')
            return String(content)
    }
}

// impressão digital dos dados de um valor (grid inteiro, ou o conteúdo de uma célula), pra saber se algo mudou de
// fato. ignora funções (callbacks são recriados a cada montagem do grid) e _updatedAt (todo new GridClass() gera
// outro); ícones do FontAwesome entram só pelo nome. objeto repetido em vários lugares é serializado em cada um —
// só referência circular (objeto dentro dele mesmo) vira marcador, senão dados diferentes poderiam dar a mesma saída.
export function fingerprintOf(value: any): string {
    const out: string[] = []
    const ancestors = new Set<any>()
    const walk = (item: any) => {
        if (item === null || item === undefined) {
            out.push('~')
            return
        }
        const type = typeof item
        if (type === 'function') {
            out.push('f')
            return
        }
        if (type === 'string') {
            out.push(JSON.stringify(item))
            return
        }
        if (type !== 'object') {
            out.push(type[0] + String(item))
            return
        }
        if (item instanceof Date) {
            out.push('D' + item.getTime())
            return
        }
        if (ancestors.has(item)) {
            out.push('@')
            return
        }
        if (typeof item.iconName === 'string' && typeof item.prefix === 'string' && Array.isArray(item.icon)) {
            out.push('I' + item.prefix + ':' + item.iconName)
            return
        }
        ancestors.add(item)
        if (Array.isArray(item)) {
            out.push('[')
            for (const child of item) {
                walk(child)
                out.push(',')
            }
            out.push(']')
        } else {
            out.push('{')
            for (const key of Object.keys(item)) {
                if (key === '_updatedAt') continue
                out.push(JSON.stringify(key), ':')
                walk(item[key])
                out.push(',')
            }
            out.push('}')
        }
        ancestors.delete(item)
    }
    walk(value)
    return out.join('')
}
